.card {
  background-color: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  box-shadow: var(--color-shadow-card);
  overflow: hidden;
  transition: box-shadow 0.2s;
  cursor: pointer;
}

.card:hover {
  box-shadow: 0 4px 16px var(--color-shadow);
}

/* Size variants */
.card.sizeLarge {
  display: flex;
  flex-direction: column;
}

.card.sizeMedium {
  display: flex;
  flex-direction: row;
  gap: 0;
}

.card.sizeSmall {
  display: flex;
  flex-direction: row;
  gap: 0;
}

.imagePlaceholder {
  background-color: var(--color-bg-secondary);
  background-image: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 10px,
    var(--color-border) 10px,
    var(--color-border) 11px
  );
  flex-shrink: 0;
}

.storyImage {
  flex-shrink: 0;
  object-fit: cover;
  display: block;
}

.imageFigure {
  margin: 0;
  display: block;
  width: 100%;
  flex-shrink: 0;
}

.sizeLarge .imagePlaceholder,
.sizeLarge .storyImage {
  width: 100%;
  height: 240px;
}

.sizeMedium .imagePlaceholder,
.sizeMedium .storyImage {
  width: 100px;
  height: 100%;
  min-height: 80px;
}

.sizeSmall .imagePlaceholder,
.sizeSmall .storyImage {
  width: 64px;
  height: 100%;
  min-height: 60px;
}

.body {
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.categoryBadge {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background-color: var(--color-accent-subtle);
  color: var(--color-accent);
  padding: 2px 6px;
  border-radius: 3px;
}

.rankBadge {
  font-size: 0.7rem;
  color: var(--color-text-muted);
}

.title {
  margin: 0;
  color: var(--color-text-primary);
  line-height: 1.3;
}

.sizeLarge .title {
  font-size: 1.5rem;
}

.sizeMedium .title {
  font-size: 1rem;
}

.sizeSmall .title {
  font-size: 0.85rem;
}

.excerpt {
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin: 0;
  /* Clamp to 2 lines to ensure date is always visible */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.sizeMedium .excerpt {
  -webkit-line-clamp: 1;
}

.sizeSmall .excerpt {
  -webkit-line-clamp: 1;
}

.footer {
  margin-top: auto;
  font-size: 0.78rem;
  color: var(--color-text-muted);
}

/* ── Image-left horizontal layout for solo stories with images ─────────── */
.card.imageLeft.sizeLarge {
  flex-direction: row;
  min-height: 220px;
}

.card.imageLeft.sizeLarge .storyImage {
  flex: 0 0 38%;
  width: 38%;
  height: auto;
  min-height: 220px;
  align-self: stretch;
}

/* ── No-image solo: wide/flat layout when the card fills a full row ──────
   @container fires when the wrapper (.soloNoImage) is ≥500px wide, which
   only happens when the card is alone on its row (pairs/triples stay ≤440px
   on the same screen widths). The threshold sits safely between the two. ── */
@container (min-width: 500px) {
  .card.noImageSolo {
    flex-direction: row;
    align-items: stretch;
  }

  .card.noImageSolo .body {
    display: flex;
    flex-direction: column;
    gap: 10px;
    overflow: visible;
    padding: 20px 24px;
  }

  .card.noImageSolo .title {
    font-size: 1.75rem;
  }

  .card.noImageSolo .excerpt {
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
    flex: 1;
    font-size: 0.95rem;
  }
}

/* ── No-image solo: narrow cards (2–3 per row) — keep square-ish look ─── */
@container (max-width: 499px) {
  .card.noImageSolo .excerpt {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    flex: 1;
  }

  .card.noImageSolo .body {
    overflow: hidden;
  }
}
